import { DateTime, Duration } from "luxon"
import { WorkDayValues } from "./types"
import { TimeSheetEntryInput } from "@oysterhr/schema"

export const presentHoursHigherThan = (
  entries: WorkDayValues[] | TimeSheetEntryInput[],
  threshold: number,
) => {
  return (
    entries
      .reduce((acc: Duration, entry: WorkDayValues | TimeSheetEntryInput) => {
        if (!entry.from || entry.from === "" || !entry.to || entry.to === "") {
          return acc
        }
        const duration = timeStringToDuration(entry.to).minus(timeStringToDuration(entry.from))
        return acc.plus(duration)
      }, Duration.fromISO("PT0S"))
      .shiftTo("hours").hours > threshold
  )
}

interface TimeSheetEntryTimes {
  from?: string
  to?: string
  breakIntervalFrom?: string
  breakIntervalTo?: string
}

export const sumWorkedHours = (entries: TimeSheetEntryTimes[]) => {
  return entries
    .reduce((acc: Duration, entry: TimeSheetEntryTimes) => {
      if (!entry?.from || entry.from === "" || !entry?.to || entry.to === "") {
        return acc
      }

      const startDuration = entry.to.includes("T00:00")
        ? Duration.fromISO("PT24H")
        : Duration.fromISO("PT0S")

      let duration = startDuration
        .plus(timeStringToDuration(entry.to))
        .minus(timeStringToDuration(entry.from))

      if (entry.breakIntervalTo && entry.breakIntervalFrom) {
        duration = duration
          .minus(timeStringToDuration(entry.breakIntervalTo))
          .plus(timeStringToDuration(entry.breakIntervalFrom))
        if (entry.breakIntervalTo.includes("T00:00")) {
          duration = duration.minus(Duration.fromISO("PT24H"))
        }
      }
      return acc.plus(duration)
    }, Duration.fromISO("PT0S"))
    .shiftTo("hours")
    .toISO()
}

export interface Range {
  start: number
  end: number
}

export const overlapsRange = (range1: Range, range2: Range) => {
  const { start: start1, end: end1 } = range1
  const { start: start2, end: end2 } = range2

  return (start1 <= start2 && start2 < end1) || (start2 <= start1 && start1 < end2)
}

export const getMillisRange = (entry: WorkDayValues | TimeSheetEntryInput): Range => {
  if (!entry.from || !entry.to) {
    throw new Error("Both 'from' and 'to' fields must be present")
  }

  return {
    start: DateTime.fromISO(entry.from).toMillis(),
    end: DateTime.fromISO(adjustToNextDay(entry.to)).toMillis(),
  }
}

export const getHoursDuration = (from?: string, to?: string) => {
  if (from && to) {
    const duration = to.includes("T00:00") ? Duration.fromISO("PT24H") : Duration.fromISO("PT0S")
    return duration
      .plus(timeStringToDuration(to))
      .minus(timeStringToDuration(from))
      .shiftTo("hours", "minute")
      .toISO()
  }
}

export const rangesOverlap = (ranges: Range[]) => {
  for (let i = 0; i < ranges.length; i++) {
    for (let j = i + 1; j < ranges.length; j++) {
      if (overlapsRange(ranges[i], ranges[j])) {
        return true
      }
    }
  }
  return false
}

export const isRangeInside = (innerRange: Range, outerRange: Range) => {
  return innerRange.start >= outerRange.start && innerRange.end <= outerRange.end
}

export const adjustToNextDay = (time: string) =>
  time.includes("T00:00") ? DateTime.fromISO(time).plus({ days: 1 }).toISO() : time

export const breakIntervalRangeValid = (
  from: string,
  to: string,
  breakIntervalFrom?: string,
  breakIntervalTo?: string,
) => {
  if (!breakIntervalFrom || !breakIntervalTo || !from || !to) {
    return true
  }

  return isRangeInside(
    {
      start: DateTime.fromISO(breakIntervalFrom).toMillis(),
      end: DateTime.fromISO(adjustToNextDay(breakIntervalTo)).toMillis(),
    },
    {
      start: DateTime.fromISO(from).toMillis(),
      end: DateTime.fromISO(adjustToNextDay(to)).toMillis(),
    },
  )
}

const timeStringToDuration = (time: string) => {
  return Duration.fromMillis(DateTime.fromISO(time).toMillis())
}
